import React, { useEffect, useState } from "react";
import axios from "../axios";
import { getDayType, DATE_BADGE } from "../utils/dayType";
import { useDayTypeVersion } from "./DayTypeProvider";
// 編集フォームはダッシュボード（SightingModal）と共通
import SightingEditForm, { IconPencil } from "./SightingEditForm";

const IconX = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const IconUser = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);
const IconClock = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);
const IconChat = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

/**
 * 過去データ（8 日より前）の目撃履歴。
 *
 * **編集できるのは super_admin だけ**（2026-09-26〜）。ダッシュボードは直近
 * 8 日しか見せないので、それより前の投稿は直す入口が無かった（bot の対応表の
 * 誤りで 30 分ずれた投稿を直せなかった）。投稿者本人の編集は 8 日より前を
 * 本体が弾くので、ここには出さない。
 */
function ArchiveModal({ isOpen, onClose, onUpdated, depot, bus_number, date, sightings = [], currentUser }) {
  // 特別日が API から届いたら描き直す（日付の色分けが getDayType に依存している）
  useDayTypeVersion();

  const isSuperAdmin = currentUser?.user_role_id === 1;
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (isOpen) {
      setEditingId(null);
      setMessage("");
    }
  }, [isOpen]);

  const [routes, setRoutes] = useState([]);

  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        if (depot) {
          const res = await axios.get(`/routes?depot=${encodeURIComponent(depot)}&mode=archive`);
          setRoutes(res.data);
        } else {
          setRoutes([]);
        }
      } catch (err) {
        console.error("路線取得失敗:", err);
      }
    };
    fetchRoutes();
  }, [depot]);

  if (!isOpen) return null;

  const getColor = (name) => {
    if (!routes.length) return "#64748b";
    const match = routes.find((r) => r.full_name === name || r.short_name === name);
    return match ? match.color : "#64748b";
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${year.slice(-2)}/${month}/${day}`;
  };

  const sortedSightings = [...sightings].sort((a, b) => (a.time || "").localeCompare(b.time || ""));
  const badge = DATE_BADGE[getDayType(date)];

  return (
    <div
      style={{
        position: "fixed", inset: 0,
        backgroundColor: "rgba(2,12,8,0.65)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        zIndex: 1000,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "16px", boxSizing: "border-box",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "24px",
          width: "100%",
          maxWidth: "480px",
          maxHeight: "90dvh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 32px 80px rgba(0,0,0,0.35), 0 4px 16px rgba(0,0,0,0.12)",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div style={{
          background: "linear-gradient(170deg, #d1fae5 0%, #a7f3d0 100%)",
          borderBottom: "1px solid #6ee7b7",
          flexShrink: 0,
        }}>
          <div style={{ padding: "10px 0 0", display: "flex", justifyContent: "center" }}>
            <div style={{ width: 40, height: 4, borderRadius: 99, background: "#047857" }} />
          </div>
          <div style={{
            padding: "10px 16px 11px",
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
              <span style={{
                fontSize: "22px", fontWeight: "800", color: "#064e3b",
                letterSpacing: "-0.5px", lineHeight: 1,
              }}>
                {depot}{bus_number}
              </span>
              <span style={{
                fontSize: "12px", fontWeight: "700",
                background: badge.bg,
                border: `1px solid ${badge.border}`,
                color: badge.color,
                borderRadius: "20px", padding: "3px 10px",
                letterSpacing: "0.3px", flexShrink: 0,
              }}>
                {formatDisplayDate(date)}
              </span>
            </div>
            <button
              onClick={onClose}
              style={{
                background: "#fff1f2",
                border: "1px solid #fecdd3",
                borderRadius: "50%",
                width: 36, height: 36,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", color: "#be123c", flexShrink: 0,
                WebkitTapHighlightColor: "transparent",
              }}
            >
              <IconX size={17} />
            </button>
          </div>
        </div>

        {/* スクロールエリア */}
        <div style={{ overflowY: "auto", overflowX: "hidden", WebkitOverflowScrolling: "touch", flex: 1 }}>
          <div style={{ padding: "16px" }}>

            {/* 目撃履歴ラベル */}
            <div style={{
              fontSize: "11px", fontWeight: "700", color: "#10b981",
              letterSpacing: "1.2px", textTransform: "uppercase",
              marginBottom: "12px",
              display: "flex", alignItems: "center", gap: "8px",
            }}>
              <span style={{
                display: "inline-block", width: 3, height: 14,
                background: "linear-gradient(180deg,#059669,#34d399)",
                borderRadius: 2,
              }} />
              目撃履歴 {sortedSightings.length}件
            </div>

            {message && (
              <div role="alert" style={{
                marginBottom: "12px", padding: "10px 12px", borderRadius: "10px",
                background: "#fef2f2", border: "1px solid #fecaca",
                color: "#991b1b", fontSize: "13px",
              }}>
                {message}
              </div>
            )}

            {sortedSightings.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {sortedSightings.map((s) => (
                  <div
                    key={s.postId || s.id}
                    style={{
                      borderRadius: "14px",
                      border: "1px solid #e8f0eb",
                      background: "#fff",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.04)",
                      overflow: "hidden",
                    }}
                  >
                    {editingId === s.postId ? (
                      <SightingEditForm
                        record={s}
                        date={date}
                        busNumber={bus_number}
                        routes={routes}
                        isSuperAdmin={isSuperAdmin}
                        onSaved={(text) => { setEditingId(null); onUpdated?.(text); }}
                        onCancel={() => setEditingId(null)}
                        onError={(text) => setMessage(text)}
                      />
                    ) : (
                    <div style={{ padding: "13px 13px 11px" }}>
                      {/* 路線バッジ */}
                      <div style={{
                        display: "inline-block",
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        verticalAlign: "top",
                        background: getColor(s.route_full_name || s.route),
                        color: "#fff", borderRadius: "20px",
                        padding: "3px 10px", fontSize: "11px", fontWeight: "800",
                        marginBottom: "8px",
                        letterSpacing: "0.2px",
                      }}>
                        {s.route_full_name || s.route}
                      </div>

                      {/* 始発 + 時刻 */}
                      <div style={{
                        display: "flex", alignItems: "baseline", gap: "4px",
                        overflow: "hidden",
                        marginBottom: s.comment ? "8px" : "10px",
                      }}>
                        <div style={{
                          flex: "0 1 auto", minWidth: 0,
                          fontSize: "16px", fontWeight: "700", color: "#0f172a",
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          letterSpacing: "-0.2px",
                        }}>
                          {s.start_full_name || s.start}
                        </div>
                        <div style={{
                          flexShrink: 0,
                          color: "#059669", fontSize: "16px", fontWeight: "800",
                          whiteSpace: "nowrap",
                          fontVariantNumeric: "tabular-nums",
                          letterSpacing: "-0.5px", lineHeight: 1,
                        }}>
                          {s.time ? s.time.slice(0, 5) : ""}
                        </div>
                      </div>

                      {/* コメント */}
                      {s.comment && (
                        <div style={{
                          display: "flex", alignItems: "flex-start", gap: "8px",
                          fontSize: "13px", color: "#475569",
                          marginBottom: "10px",
                          borderLeft: "2.5px solid #34d399",
                          paddingLeft: "10px",
                        }}>
                          <span style={{ flexShrink: 0, color: "#10b981", marginTop: "2px" }}>
                            <IconChat size={12} />
                          </span>
                          <span style={{ lineHeight: 1.5 }}>{s.comment}</span>
                        </div>
                      )}

                      {/* ユーザー・投稿時刻（左）／ super_admin の編集（右） */}
                      <div style={{
                        display: "flex", alignItems: "center",
                        justifyContent: "space-between", gap: "8px",
                        paddingTop: "8px",
                        borderTop: "1px solid #f1f5f4",
                      }}>
                        <div style={{
                          display: "flex", flexDirection: "column", gap: "3px",
                          fontSize: "11.5px", color: "#94a3b8", minWidth: 0,
                        }}>
                          <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            <IconUser size={11} />
                            {s.userId ? (
                              <a href={`/users/${s.userId}`} style={{ color: "#3b82f6", textDecoration: "none", fontWeight: "500" }}>
                                {s.user || "名無し"}
                              </a>
                            ) : (
                              <span>{s.user || "名無し"}</span>
                            )}
                          </span>
                          <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            <IconClock size={11} />{s.postedAt || "??/??"}
                          </span>
                        </div>
                        {isSuperAdmin && (
                          <button
                            type="button"
                            onClick={() => { setMessage(""); setEditingId(s.postId); }}
                            style={{
                              display: "flex", alignItems: "center", gap: "4px",
                              padding: "5px 11px", borderRadius: "8px",
                              border: "1px solid #a7f3d0",
                              background: "#ecfdf5", color: "#065f46",
                              cursor: "pointer", fontSize: "12px", fontWeight: "600",
                              flexShrink: 0,
                              WebkitTapHighlightColor: "transparent",
                            }}
                          >
                            <IconPencil size={12} /> 編集
                          </button>
                        )}
                      </div>
                    </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: "center", color: "#94a3b8", fontSize: "14px", padding: "32px 0" }}>
                目撃情報がありません
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ArchiveModal;
